/**
 * Live geo-evidence capture (S5-9/S5-10; RBI-04 · IFSCA-06/07).
 *
 * The droom posts the device's own geolocation fix (or an HONEST missing state
 * — DENIED/UNSUPPORTED/TIMEOUT — never fabricated coordinates) to the session's
 * geo-evidence rail at join. The server derives the source IP from the edge
 * chain and evaluates geofence / GPS-vs-IP / VPN signals against the session's
 * pinned regulatory snapshot; the client NEVER sends an IP or a verdict
 * (client.ts security posture: client-supplied geo/IP headers are not trusted).
 */

import { isMockMode, request } from './client';

export type GeoPermissionState =
  | 'GRANTED'
  | 'DENIED'
  | 'UNSUPPORTED'
  | 'TIMEOUT'
  | 'ERROR';

export interface GeoCaptureBody {
  lat?: number;
  lon?: number;
  accuracyM?: number;
  capturedAtMs?: number;
  permissionState: GeoPermissionState;
  clientHints?: { mocked: boolean | null };
  recapture?: boolean;
}

/** The evidence row echoed by the backend (verdicts are advisory — officer-facing). */
export interface GeoEvidence {
  id: string;
  sessionId: string;
  role: 'CUSTOMER' | 'AGENT';
  attempt: number;
  gpsCountry: string | null;
  ipCountry: string | null;
  geofence: 'IN_JURISDICTION' | 'OUT' | 'UNKNOWN';
  gpsIpMatch: 'MATCH' | 'MISMATCH' | 'UNKNOWN';
  vpnSignal: 'DATACENTER_ASN' | 'NONE' | 'UNKNOWN';
  advisory: 'PASS' | 'REVIEW' | 'FAIL';
  sha256: string;
}

/**
 * POST the capture. Mock mode (no backend) resolves null WITHOUT requesting the
 * browser permission at all — no evidence rail exists to bind a location to, so
 * we do not collect one (data minimisation).
 */
export async function postGeoEvidence(
  sessionId: string,
  body: GeoCaptureBody,
): Promise<GeoEvidence | null> {
  if (isMockMode()) return null;
  return request<GeoEvidence>(
    `/api/v1/sessions/${encodeURIComponent(sessionId)}/geo`,
    { method: 'POST', body },
  );
}
